import * as Sentry from '@sentry/browser';
import {
	isHtmlDocumentScriptError,
	isInjectedScriptError,
	isThrownEventObject,
	isUnhandledEmptyObjectRejection,
} from './sentry-noise-filter';

/*
 * Runtime configuration injected by the control panel in the page head
 * (see cp/includes/php/header.php and cp/login.php for the emitting side).
 * Keeping the DSN, environment and release in PHP .env mirrors the server-side
 * Sentry bootstrap and avoids baking environment-specific values into the bundle.
 */
const objConfig = window.sentryBrowserConfig;
const strDsn = objConfig && objConfig.dsn ? objConfig.dsn : '';

/*
 * No DSN configured means Sentry is not set up on this host, so skip init
 * entirely and leave the SDK adding zero overhead.
 */
if ( strDsn ) {
	Sentry.init( {
		dsn: strDsn,
		environment: objConfig.environment || 'production',
		release: objConfig.release || undefined,
		// Same-origin tunnel: Firefox ETP blocks ingest.*.sentry.io as a tracker.
		tunnel: objConfig.tunnel || '/sentry-tunnel',

		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration( {
				/*
				 * The control panel renders customer data, so record structure
				 * only: mask every text node and input value and block all media.
				 */
				maskAllText: true,
				maskAllInputs: true,
				blockAllMedia: true,
			} ),
		],

		/*
		 * Modest transaction sampling keeps page-load and navigation tracing
		 * from overwhelming a busy production host; overridable from the config.
		 */
		tracesSampleRate: typeof objConfig.tracesSampleRate === 'number' ? objConfig.tracesSampleRate : 0.2,

		/*
		 * Attach the sentry-trace and baggage headers to same-origin control-panel
		 * requests so browser transactions stitch onto the PHP backend trace,
		 * which already continues those headers in includes/php/sentry.php.
		 * The second pattern requires at least one subdomain so it matches control-panel
		 * hosts (e.g. dev.mijn.host) but never the apex mijn.host.
		 * The apex serves the marketing WordPress site, whose cross-origin blog feed
		 * (dashboard.js) is a simple GET; adding trace headers would turn it into a
		 * preflighted request that the feed rejects, and WordPress never continues our trace anyway.
		 */
		tracePropagationTargets: [
			/^\//,
			/^https:\/\/([a-z0-9-]+\.)+mijn\.host/,
		],

		/*
		 * Session Replay records a small share of all sessions plus every
		 * session that runs into an error.
		 */
		replaysSessionSampleRate: typeof objConfig.replaysSessionSampleRate === 'number' ? objConfig.replaysSessionSampleRate : 0.1,
		replaysOnErrorSampleRate: 1.0,

		enableLogs: true,

		/*
		 * Drop non-actionable browser noise before it reaches Sentry. Four
		 * classes are filtered, and every real Error passes through untouched:
		 *
		 * 1. A DOM/jQuery Event object thrown instead of an Error. It carries no
		 *    stack and no source location, so it cannot be debugged and only
		 *    floods the project. The value-string check is a fallback for
		 *    onerror paths that expose only the serialized exception, and it
		 *    additionally requires event-key markers so it never drops a
		 *    genuinely thrown plain object.
		 * 2. An unhandled rejection whose reason is an empty object or a
		 *    CustomEvent that serializes as one (Safari 26). See
		 *    isUnhandledEmptyObjectRejection.
		 * 3. An uncaught error injected by a browser extension, content blocker
		 *    or in-app browser, whose only stack frame is the top-level document
		 *    (no script file, no function). See isInjectedScriptError.
		 * 4. An unhandled SyntaxError from a lazily-imported component chunk that
		 *    was served an HTML document instead of JavaScript (stale chunk after
		 *    a deploy, an edge interstitial, or a bot). See
		 *    isHtmlDocumentScriptError.
		 */
		beforeSend ( event, hint ) {
			if ( hint && isThrownEventObject( hint.originalException ) ) {
				return null;
			}

			if ( isUnhandledEmptyObjectRejection( event, hint ) ) {
				return null;
			}

			if ( isInjectedScriptError( event ) ) {
				return null;
			}

			if ( isHtmlDocumentScriptError( event ) ) {
				return null;
			}

			const arrValues = event && event.exception && event.exception.values;

			if ( arrValues ) {
				for ( const objValue of arrValues ) {
					const strValue = objValue && objValue.value;

					if ( typeof strValue === 'string'
						&& strValue.indexOf( 'Object captured as exception' ) === 0
						&& ( /\b(isTrigger|delegateTarget|handleObj|rnamespace|currentTarget)\b/ ).test( strValue ) ) {
						return null;
					}
				}
			}

			return event;
		},
	} );
}
